/* eslint-disable react/no-multi-comp */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import { withRouter } from 'react-router-dom';
import clsx from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import styles from './../../../style';
import moment from 'moment';
import { getPastSoapNoteById } from 'services/soap/action';
import {
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Box,
  Modal,
  Avatar
} from '@material-ui/core';
import {
  LastPage,
  CloseOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
  NavigateNext,
  NavigateBefore
} from '@material-ui/icons';
import Loader from '../../../../Common/components/Loader';
import { connect } from 'react-redux';
import { EncounterSummary } from './';
import MessageBox from 'util/messageBox';
import { Skeleton } from "@material-ui/lab";

const SoapPast = ({ classes, patientId, patientInfo, getPastSoapNoteById, soapNotes, ...props }) => {
  const [openSoap, setOpenSoap] = useState({ open: false, soap: null });
  const [isHistory, setIsHistory] = useState(false);
  const [page, setPage] = useState(0);
  const [pastSoapNote, setPastSoapNote] = useState(null);
  const [pastSoapNoteLoading, setPastSoapNoteLoading] = useState(true);
  const [succmsg, setSuccMsg] = useState({ succ: false, succMsg: '' });
  const [errmsg, setErrMsg] = useState({ err: false, errMsg: '' });

  // Fetch Past SOAP Notes
  useEffect(() => {
    if (!patientId) return;
    const fetchData = async () => {
      try {
        setPastSoapNoteLoading(soapNotes?.content ? false : true);
        setPastSoapNote(soapNotes);
      } catch (error) {
        setErrMsg({ err: true, errMsg: 'Failed to load past SOAP notes.' });
      }
    };
    fetchData();
  }, [soapNotes]);

  const toggleHistory = useCallback(() => {
    setIsHistory(prev => !prev);
  }, []);

  const handleOpenSoap = useCallback(item => {
    setOpenSoap({ open: true, soap: item });
  }, []);

  const handleCloseSoap = useCallback(() => {
    setOpenSoap({ open: false, soap: null });
  }, []);

  const handleCopyPast = useCallback((soap, pastSoapNoteDetails) => {
    props.handleCopyPast?.(soap, pastSoapNoteDetails);
    setOpenSoap({ open: false, soap: null });
  }, [props]);

  const handleEditPast = useCallback((soap, pastSoapNoteDetails) => {
    props.handleEditPast?.(soap, pastSoapNoteDetails);
    setOpenSoap({ open: false, soap: null });
  }, [props]);

  const showSuccessMsg = useCallback(msg => {
    setSuccMsg({ succ: true, succMsg: msg });
  }, []);

  const handleChange = useCallback(async nav => {
    let pastPage = page;
    setPage(prevPage => {
      if (nav === 'next') { pastPage = prevPage + 1; return prevPage + 1; }
      if (nav === 'first') { pastPage = 0; return 0; }
      pastPage = Math.max(prevPage - 1, 0);
      return Math.max(prevPage - 1, 0);
    });
    getPastSoapNoteById(patientId, nav === 'next' ? pastPage : nav === 'first' ? 0 : Math.max(pastPage, 0))
      .then(res => {
        setPastSoapNote(res);
      })
      .catch(() => {
        setErrMsg({ err: true, errMsg: 'Failed to load past SOAP notes.' });
      });
  }, []);

  const renderSuccess = useMemo(
    () =>
      succmsg.succ ? (
        <MessageBox
          message={succmsg.succMsg}
          onClose={() => setSuccMsg({ succ: false })}
          open={succmsg.succ}
          variant="success"
        />
      ) : null,
    [succmsg]
  );

  const renderError = useMemo(
    () =>
      errmsg.err ? (
        <MessageBox
          message={errmsg.errMsg}
          onClose={() => setErrMsg({ err: false })}
          open={errmsg.err}
          variant="error"
        />
      ) : null,
    [errmsg]
  );

  const TableRowsLoader = ({ rowsNum = 20 }) => (
    <>
      {[...Array(rowsNum)].map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton animation="wave" variant="text" /></TableCell>
          <TableCell><Skeleton animation="wave" variant="text" /></TableCell>
          <TableCell><Skeleton animation="wave" variant="text" /></TableCell> 
        </TableRow>
      ))}
    </>
  );

  const content = pastSoapNote?.content ?? [];
  const totalPages = (pastSoapNote && pastSoapNote?.content) ? pastSoapNote?.totalPages ?? 0 : null;

  return (
    <Box className={classes.soapPastWrap}>
      <Box className={classes.soapPastHeader}>
        <Typography style={{ display: 'flex' }}>
          Past SOAP Notes
          {totalPages > 1 && (
            <>
              <IconButton disabled={page === 0} onClick={() => handleChange('prev')} size="small">
                <NavigateBefore />
              </IconButton>
              <Avatar style={{ width: 20, height: 20, top: 5, fontSize: 12 }}>{page + 1}</Avatar>
              <IconButton
                disabled={pastSoapNote?.last === true}
                onClick={() => handleChange('next')}
                size="small"
              >
                <NavigateNext />
              </IconButton>
            </>
          )}
        </Typography>
        {!props.isPast && (
          <IconButton onClick={() => props.setPast?.()} size="small">
            <LastPage />
          </IconButton>
        )}
      </Box>

      <Box>
        <PerfectScrollbar style={{ height: '90vh' }}>
          <Table className={clsx(classes.plainTable, classes.blueBg)}>
            <TableBody>
              {pastSoapNoteLoading && <TableRowsLoader />}
              {totalPages === 0 && <TableRow><TableCell>No records found</TableCell></TableRow>}
              {Array.isArray(content) &&
                content.map(item => (
                  <TableRow key={item.id || item.visitdate} onClick={() => handleOpenSoap(item)}>
                    <TableCell>
                      {!item.signed && (
                        <span
                          style={{
                            height: 5,
                            width: 5,
                            borderRadius: 3,
                            backgroundColor: 'red',
                            display: 'inline-block',
                            position: 'absolute',
                            left: 6
                          }}
                        />
                      )}
                      {item.visitdate ? moment(item.visitdate).format('MM/DD/YYYY') : ''}
                    </TableCell>
                    <TableCell>
                      {item.soapnote?.subjective?.substring(0, 50) || ''}
                    </TableCell>
                    <TableCell>
                      {item.signed
                        ? `Signed by: ${item.provider?.lastname || ''} ${item.provider?.firstname || ''}`
                        : item.routed
                          ? `Routed to: ${item.provider?.lastname || ''} ${item.provider?.firstname || ''}`
                          : `Created by: ${item.provider?.lastname || ''} ${item.provider?.firstname || ''}`}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </PerfectScrollbar>
      </Box>

      <Modal
        aria-describedby="simple-modal-description"
        aria-labelledby="simple-modal-title"
        disableBackdropClick
        onClose={handleCloseSoap}
        open={Boolean(openSoap.open)}
      >
        <Box className={classes.modalWrap} style={{ width: '95%', maxWidth: 1200 }}>
          <Box className={classes.modalHead}>
            <Typography style={{ display: 'flex', alignItems: 'center' }} variant="h2">
              {openSoap.soap?.visitdate
                ? `Encounter Date: ${moment(openSoap.soap.visitdate).format('MM/DD/YYYY')}`
                : ''}
            </Typography>
            <span>
              <Button
                color="primary"
                onClick={toggleHistory}
                size="small"
                startIcon={isHistory ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
                style={{ textTransform: 'capitalize' }}
                variant="outlined"
              >
                {isHistory ? 'Show' : 'Hide'}&nbsp;Medical History
              </Button>
              <IconButton aria-label="delete" onClick={handleCloseSoap}>
                <CloseOutlined />
              </IconButton>
            </span>
          </Box>
          <EncounterSummary
            {...props}
            page={page}
            isHistory={isHistory}
            showSuccessMsg={showSuccessMsg}
            handleCopyPast={handleCopyPast}
            handleEditPast={handleEditPast}
            soap={openSoap.soap}
            setHistory={toggleHistory}
            onCancel={handleCloseSoap}
          />
        </Box>
      </Modal>

      {renderSuccess}
      {renderError}
    </Box>
  );
};

SoapPast.propTypes = {
  className: PropTypes.string
};

const mapDispatchToProps = { getPastSoapNoteById };

const mapStateToProps = state => ({
  ehrID: state.profile?.ehrID,
  userID: state.profile?.userID,
  patientId: state.soap?.patientInfo?.id,
  soapNotes: state.soap && state.soap.pastSoapNote,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(SoapPast))
);
